import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Chip,
} from '@mui/material';
import {
  Check as CheckIcon,
  Engineering as EngineeringIcon,
} from '@mui/icons-material';

export interface Toolkit {
  id: string;
  code: string;
  name: string;
  icon: React.ReactNode;
  description: string;
  price: {
    monthly: number;
    annual: number;
  };
  features: string[];
  agents: string[];
  compliance?: string[];
  available: boolean;
  comingSoon?: boolean;
}

const toolkits: Toolkit[] = [
  {
    id: 'mep_toolkit',
    code: 'MEP_TOOLKIT',
    name: 'MEP Engineering',
    icon: <EngineeringIcon style={{ fontSize: 24 }} />,
    description: 'Mechanical, Electrical & Plumbing',
    price: {
      monthly: 129,
      annual: 1290,
    },
    features: [
      'HVAC calculations',
      'Electrical analysis',
      'CAD processing',
      'Climate data',
    ],
    agents: ['CADex', 'ThermaCalc', 'VoltMaster', 'SyncMEP', 'CodeGuard'],
    compliance: ['ASHRAE', 'NEC', 'IBC', 'IECC'],
    available: true,
  },
];

interface ToolkitSelectionProps {
  selectedToolkitId?: string;
  onSelect: (toolkitCode: string) => void;
  billingFrequency: 'monthly' | 'annual';
}

export const ToolkitSelection: React.FC<ToolkitSelectionProps> = ({
  selectedToolkitId,
  onSelect,
  billingFrequency,
}) => {
  return (
    <div style={{ marginTop: 16 }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Select Your Industry Toolkit
      </Typography>
      <Typography variant="body2" color="text.secondary" style={{ marginBottom: 16 }}>
        Choose your specialized toolkit - included with Industry Specific tier at no additional cost
      </Typography>

      <Grid container spacing={2}>
        {toolkits.map((toolkit) => {
          const isSelected = selectedToolkitId === toolkit.code;
          const isDisabled = !toolkit.available;

          return (
            <Grid item xs={12} sm={6} md={3} key={toolkit.id}>
              <Card
                style={{
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.6 : 1,
                  border: isSelected ? '2px solid #325BA8' : '2px solid rgba(0,0,0,0.12)',
                  backgroundColor: isSelected ? 'rgba(50,91,168,0.04)' : '#fff',
                  transition: 'all 0.2s',
                  position: 'relative',
                }}
                onClick={() => {
                  if (!isDisabled) {
                    console.log('Toolkit selected:', toolkit.code);
                    onSelect(toolkit.code);
                  }
                }}
              >
                {toolkit.comingSoon && (
                  <Chip
                    label="SOON"
                    size="small"
                    style={{
                      position: 'absolute',
                      top: 8,
                      right: 8,
                      height: 20,
                      fontSize: '0.7rem',
                      backgroundColor: '#ed6c02',
                      color: 'white',
                    }}
                  />
                )}

                {isSelected && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 8,
                      left: 8,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      backgroundColor: '#325BA8',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CheckIcon style={{ color: 'white', fontSize: 16 }} />
                  </div>
                )}

                <CardContent style={{ padding: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                    <div style={{ color: isDisabled ? 'rgba(0,0,0,0.38)' : '#325BA8', marginRight: 8 }}>
                      {toolkit.icon}
                    </div>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {toolkit.name}
                    </Typography>
                  </div>

                  <Typography variant="caption" color="text.secondary" display="block" style={{ marginBottom: 8 }}>
                    {toolkit.description}
                  </Typography>

                  <div style={{ marginBottom: 8 }}>
                    <Chip
                      label="INCLUDED"
                      color="success"
                      size="small"
                      style={{ height: 20 }}
                    />
                  </div>

                  <div style={{ marginBottom: 8 }}>
                    <Typography variant="caption" fontWeight="bold" display="block">
                      {toolkit.agents.length} Agents
                    </Typography>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginTop: 4 }}>
                      {toolkit.agents.slice(0, 3).map((agent, idx) => (
                        <Chip
                          key={agent}
                          label={agent}
                          size="small"
                          style={{
                            height: 16,
                            fontSize: '0.65rem',
                          }}
                        />
                      ))}
                      {toolkit.agents.length > 3 && (
                        <Typography variant="caption" color="text.secondary">
                          +{toolkit.agents.length - 3}
                        </Typography>
                      )}
                    </div>
                  </div>

                  {toolkit.compliance && (
                    <div>
                      <Typography variant="caption" color="text.secondary">
                        {toolkit.compliance.join(' • ')}
                      </Typography>
                    </div>
                  )}
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </div>
  );
};

export default ToolkitSelection;
